import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumPeriodoLiquidacion } from '../../api'
import { FlexibilitiesModel } from '../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import {
  PriceCoefficientAPI,
  PriceCoefficientTable,
  PriceCoefficientValueModel
} from '../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import {
  CreatePriceComponentRequest,
  PriceComponentAPI,
  PriceComponentV2RequestModel
} from '../../api/Argentia/endpoints/priceComponents'
import {
  PostPricePeriodValueGroupedModel,
  PricePeriodValueGroupedModel,
  PricePeriodValueRequestModel
} from '../../api/Argentia/endpoints/pricing/model/values'
import { EnumPeriodo } from '../../api/Argentia/enums/periodo'
import { EnumPriceType } from '../../api/oData/enums/enumPriceType'
import { enumEnumToValueNumber, generateUniqueKeyWithNoPrefix } from '../../helpers'
import { GridRows } from '../../helpers/gridValuesGenerator/gridValuesGenerator'
import { getActiveCompany } from '../../redux'
import { PriceComponentFormData } from '../pricing/priceComponentType/components/priceComponentTypeForm'
import { PriceComponentForm } from './add/priceComponentsAdd'

export const priceComponentsSelectOptionsMapper = ({
  priceComponents,
  filterByComponentType
}: {
  priceComponents: PriceComponentAPI[]
  filterByComponentType?: number[]
}) => {
  let filteredComponents = [] as PriceComponentAPI[]

  filteredComponents =
    filterByComponentType && filterByComponentType.length > 0
      ? priceComponents.filter(
          component => !filterByComponentType.includes(component.idPriceComponentType)
        )
      : priceComponents

  return filteredComponents.map(priceComponent => ({
    value: priceComponent.id,
    label: `${priceComponent.typeCode} - ${priceComponent.typeDescription}`
  }))
}

export const newPriceComponentRequest = (form: PriceComponentForm): CreatePriceComponentRequest => {
  const priceComponent: CreatePriceComponentRequest['priceComponent'] = {
    idContract: form.flexibilityId && form.flexibilityId > 0 ? undefined : form.contractID,
    idFlexibility: form.flexibilityId && form.flexibilityId > 0 ? form.flexibilityId : undefined,
    idPriceComponentType: form.priceComponentTypeID
  }

  const price: CreatePriceComponentRequest['price'] = {
    company: getActiveCompany()?.id ?? 1,
    commodity: form.commodity,
    priceType: form.priceType,
    code: form.priceCode,
    description: form.priceComponentTypeDescription ?? form.priceCode,
    priceCalculationType: null,
    // marketType: 1,
    startDate: moment(form.startDate).startOf('year').format('YYYY-MM-DD'),
    expression: null,
    priceUnit: form.priceUnit,
    round: form.round,
    value: form.value ?? 0 // Price Value needs to be created.
  }

  return {
    priceComponent,
    price
  }
}

export const newPriceComponentRequestV2 = (
  form: PriceComponentForm
): PriceComponentV2RequestModel => {
  return {
    price: {
      priceType: form.priceType,
      unitPrice: form.priceUnit!,
      rounding: form.round,
      values: mapValuesFromFormData(form),
      expression: ''
    },
    priceComponentType: {
      id: form.priceComponentTypeID
    },
    toll: form.toll ?? undefined,
    presionTension: form.presionTension ?? undefined,
    regionImpuestos: form.regionImpuestos ?? undefined,
    lead: form.leadCode ? { code: form.leadCode } : form.lead ? { id: form.lead } : undefined,
    contract:
      form.contractID && !form.flexibilityId // If flexibility is selected, contract must not be included in API request payload
        ? {
            id: form.contractID,
            code: form.contractCode ?? undefined
          }
        : undefined,
    flexibility: form.flexibilityId
      ? {
          id: form.flexibilityId,
          description: form.flexibilityCode ?? undefined
        }
      : undefined
  }
}

export const mapValuesFromFormData = (
  formData: PriceComponentForm | PriceComponentFormData
): PricePeriodValueRequestModel[] => {
  const periodValuesMap = new Map<string, number>([
    ['period1Value', 1],
    ['period2Value', 2],
    ['period3Value', 3],
    ['period4Value', 4],
    ['period5Value', 5],
    ['period6Value', 6]
  ])

  return formData.priceType !== EnumPriceType.ParametroPeriodo
    ? [
        {
          value: formData.value ? formData.value : 0,
          startDate: moment(formData.startDate).startOf('year').format('YYYY-MM-DD')
          // endDate: formatDateOnlyString(formData.endDate)
        }
      ]
    : (Array.from(periodValuesMap.entries())
        .map(
          ([key, period]) =>
            isValidPeriodValue(formData[key]) && {
              value: formData[key],
              startDate: moment(formData.startDate).format('YYYY-MM-DD'),
              // endDate: formData.endDate,
              period: period
            }
        )
        .filter(
          item => item !== undefined && item !== null && item !== false
        ) as PricePeriodValueRequestModel[])
}

export const mapPricePeriodFormDataToRequest = (formData: PricePeriodValueGroupedModel) => {
  const request: PostPricePeriodValueGroupedModel = {
    values: []
  }

  // helper para quedarnos con el periodN valor. En la edición el periodo esta disponible pero al Añadir es mas fiable este metodo.
  Array.from(Object.entries(formData)).map(([key, value]) => {
    if (key.startsWith('period') && value) {
      const periodStr = key.replace('period', '')
      const periodNumber = Number(periodStr)
      if (!isNaN(periodNumber) && periodStr.trim() !== '' && isValidPeriodValue(value.value)) {
        request.values.push({
          startDate: moment(formData.startDate).format('YYYY-MM-DD'),
          endDate: formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : null,
          period: periodNumber,
          value: value.value
        } as PricePeriodValueRequestModel)
      }
    }
  })

  return request
}

const isValidPeriodValue = (value: any): boolean => {
  return value !== null && value !== undefined && value !== ''
}

export const newPriceCoefficientsRequest = (
  form: PriceCoefficientTable[]
): CreatePriceComponentRequest['priceCoefficients'] => {
  let coefficients = [] as CreatePriceComponentRequest['priceCoefficients']

  form.forEach(coef => {
    coefficients!.push({
      idSettlementPeriod: coef.idSettlementPeriod,
      startDate: moment(coef.startDate).format('YYYY-MM-DD'),
      endDate: coef.endDate ? moment(coef.endDate).format('YYYY-MM-DD') : undefined,
      periodDescription: coef.periodDescription,
      coefficientValues: mapCoefficientValues(coef)
    })
  })

  return coefficients
}

export const priceCoefficientsToAPIMapper = (
  form: PriceCoefficientTable[]
): (PriceCoefficientAPI | CreatePriceComponentRequest['priceCoefficients'])[] => {
  const coefficients: (PriceCoefficientAPI | CreatePriceComponentRequest['priceCoefficients'])[] =
    form.map(coef => {
      return {
        id: coef?.id,
        idPriceVersion: coef.idPriceVersion,
        idSettlementPeriod: coef.idSettlementPeriod,
        startDate: moment(coef.startDate).format('YYYY-MM-DD'),
        endDate: coef.endDate ? moment(coef.endDate).format('YYYY-MM-DD') : undefined,
        periodDescription: coef.periodDescription,
        coefficientValues: mapCoefficientValues(coef)
      }
    })

  return coefficients
}

export const mapCoefficientValues = (
  coefficient: PriceCoefficientTable
): PriceCoefficientValueModel[] => {
  let values: PriceCoefficientValueModel[] = []

  if (coefficient.p1 != null) {
    values.push({
      idPeriod: 1,
      value: coefficient.p1
    })
  }

  if (coefficient.p2 != null) {
    values.push({
      idPeriod: 2,
      value: coefficient.p2
    })
  }

  if (coefficient.p3 != null) {
    values.push({
      idPeriod: 3,
      value: coefficient.p3
    })
  }

  if (coefficient.p4 != null) {
    values.push({
      idPeriod: 4,
      value: coefficient.p4
    })
  }

  if (coefficient.p5 != null) {
    values.push({
      idPeriod: 5,
      value: coefficient.p5
    })
  }

  if (coefficient.p6 != null) {
    values.push({
      idPeriod: 6,
      value: coefficient.p6
    })
  }

  return values
}

export const priceComponentAPItoFormMapper = (
  priceComponent: PriceComponentAPI
): Partial<PriceComponentForm> => {
  return {
    contractID: priceComponent.idContract,
    priceComponentID: priceComponent.idPriceComponentType,
    priceComponentTypeID: priceComponent.idPriceComponentType,
    priceComponentTypeCode: priceComponent.typeCode,
    priceComponentTypeDescription: priceComponent.typeDescription,
    toll: priceComponent.toll
  }
}

export const priceCoefficientAPIListtoTableMapper = (
  priceCoefficients: PriceCoefficientAPI[]
): PriceCoefficientTable[] => {
  return priceCoefficients.map(priceCoefficientAPIItemtoTableMapper)
}

export const priceCoefficientAPIItemtoTableMapper = (
  priceCoefficient: PriceCoefficientAPI
): PriceCoefficientTable => {
  let values = {}

  priceCoefficient.coefficientValues?.forEach((coefficientValue, idx) => {
    const periodNumber = enumEnumToValueNumber(EnumPeriodo, coefficientValue.idPeriod)
    values[`p${periodNumber}`] = coefficientValue.value
  })

  return {
    ...priceCoefficient,
    idSettlementPeriod: enumEnumToValueNumber(
      EnumPeriodoLiquidacion,
      priceCoefficient.idSettlementPeriod
    )!,
    startDate: moment(priceCoefficient.startDate).toDate(),
    endDate: priceCoefficient.endDate ? moment(priceCoefficient.endDate).toDate() : undefined,
    ...values
  }
}
export const flexibilityToSelectOptions = (flexibilities: FlexibilitiesModel[]): SelectOption[] => {
  let options = flexibilities.map(flexibility => ({
    value: flexibility.id,
    label: flexibility.description ?? `Flexibilidad contrato ${flexibility.contract}`
  }))

  options.push({ value: 0, label: 'N/A' })

  return options
}

export const gridRowToPriceCoefficientList = (
  generatedData: GridRows[],
  settlementPeriod: EnumPeriodoLiquidacion
): PriceCoefficientTable[] => {
  return generatedData.map((item, idx) => {
    let description = ''
    switch (settlementPeriod) {
      case EnumPeriodoLiquidacion.Anual:
        description = `YR-${moment(item.startDate).year()}`
        break
      case EnumPeriodoLiquidacion.Mensual:
        description = `M${moment(item.endDate).month() + 1}-${moment(item.startDate).year()}`
        break
      case EnumPeriodoLiquidacion.Trimestral:
        description = `Q${moment(item.startDate).quarter()}-${moment(item.startDate).year()}`
        break
      default:
        description = `Periodo ${idx}`
    }

    return {
      tempId: generateUniqueKeyWithNoPrefix(),
      periodDescription: description,
      idSettlementPeriod: settlementPeriod,
      startDate: moment(item.startDate).toDate(),
      endDate: moment(item.endDate).toDate(),
      p1: item.quantity,
      p2: item.quantity,
      p3: item.quantity,
      p4: item.quantity,
      p5: item.quantity,
      p6: item.quantity
    } as PriceCoefficientTable
  })
}
